import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Button } from '../../common/Button';
import { HintContainer } from '../Hint/styles';
import { FieldContainer } from '../Input/styles';
import { CLOSE_TIMEOUT_MS } from './index';
import { Colors } from '../../common/Colors';
import { Variables } from '../../common/Variables';

export const modalOverlay = css`
  overflow: auto;
  position: fixed;
  z-index: 1000;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  padding: 0 2rem;
  text-align: center;
  background-color: ${Colors.ut.modalOverlay};
  opacity: 0;
  transition: opacity 0.3s;

  &:before {
    content: '';
    display: inline-block;
    vertical-align: middle;
    width: 0;
    height: 100%;
    font-size: 0;
  }
`;

export const modalOverlayAfterOpen = css`
  && {
    opacity: 1;
  }
`;

export const modalOverlayBeforeClose = css`
  && {
    transition-duration: ${`${CLOSE_TIMEOUT_MS}ms`};
    opacity: 0;
  }
`;

export const modalContentBase = css`
  outline: 0;
  position: relative;
  vertical-align: middle;
  flex-direction: column;
  min-height: 55rem;
  padding: 4.3rem 5rem 5rem;
  font-size: 1.4rem;
  line-height: 1.334;
  text-align: left;
  word-wrap: break-word;
  background-color: #fff;
  border: 1px solid ${Colors.ut.border5};
  background-clip: padding-box;
  box-shadow: ${Variables.popupShadow};
  border-radius: ${Variables.popupRadius};
`;

export const modalContent = css`
  ${modalContentBase};
  display: inline-flex;
  width: 100%;
  margin: 2rem 0;
  transform: translateY(calc(-100% - 100vh));
  transition: transform 0.6s 0.1s cubic-bezier(0.18, 0.83, 0.34, 1);
`;

export const modalContentAfterOpen = css`
  && {
    transform: translateY(0);
  }
`;

export const modalContentBeforeClose = css`
  && {
    transform: scale(0.8);
    transition-delay: 0s;
    transition-duration: ${`${CLOSE_TIMEOUT_MS}ms`};
    transition-timing-function: ease;
  }
`;

export const modalContentSmall = css`
  max-width: 43rem;
`;

export const modalContentMedium = css`
  max-width: 68rem;
`;

export const modalContentBig = css`
  max-width: 91.2rem;
`;

export const modalBodyOpen = css`
  overflow: hidden;
`;

export const ModalPreheader = styled('p')`
  background-color: rgba(0, 0, 0, 0.05);
  margin: 0 -5rem 4.3rem;
  padding: 0.5rem 0;
  font-size: 1.2rem;
  line-height: 1.54;
  color: ${Colors.text.disabled};
  text-align: center;
  letter-spacing: 3px;
  text-transform: uppercase;
  border-radius: ${Variables.popupRadius} ${Variables.popupRadius} 0 0;
`;

export const Header = styled('div')`
  margin: 0 0 2rem;
`;

export const Title = styled('p')`
  margin: 0;
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.25;
`;

export const WrapperScroll = styled('div')`
  display: flex;
  flex-direction: column;
  overflow: auto;
  flex-grow: 1;
  max-height: 46.1rem;
  box-shadow: 0 3rem 3rem -3rem rgba(190, 199, 209, 0.4) inset,
    0 -3rem 3rem -3rem rgba(190, 199, 209, 0.4) inset;
`;

export const WrapperScrollInner = styled('div')`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 4rem 5rem;
  box-shadow: 0 9rem 3rem -4rem #fff inset, 0 -9rem 3rem -4rem #fff inset;

  & > * {
    flex-shrink: 0;
  }
`;

export const FieldsWrapper = styled('div')`
  margin-bottom: 1.5rem;
`;

const link = css`
  display: inline-block;
  line-height: 1.1;
  text-decoration: none;
  color: inherit;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  transition: color 0.3s, border-color 0.3s;

  &:hover,
  &:focus {
    outline: 0;
    color: #3187ee;
    border-bottom-color: inherit;
  }
`;

export const Paragraph = styled('p')`
  margin-top: 1.6rem;
  margin-bottom: 0.9rem;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }

  & + & {
    margin-top: 1.1rem;
  }

  a {
    ${link};
  }
`;

export const Content = styled('div')`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  ${FieldContainer} + ${Paragraph} {
    margin-top: 1rem;
    margin-bottom: 2rem;
  }

  & > * {
    flex-shrink: 0;
  }
`;

export const Form = Content.withComponent('form');

export const ParagraphLink = styled('a')`
  ${link};
`;

export const Footer = styled('div')`
  margin-top: auto;
  padding-top: 3rem;
  margin-left: -1.5rem;

  & > * {
    margin-left: 1.5rem !important;
  }

  ${Paragraph} {
    margin-bottom: 1.4rem;
    color: rgba(0, 0, 0, 0.5);
  }

  ${HintContainer} {
    &:not(:last-child) {
      margin-bottom: 2rem;
    }
  }
`;

export const FooterScroll = styled(Footer)`
  padding: 1.9rem 5rem 2.7rem 3.5rem;
  margin-left: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

interface ButtonCloseProps {
  preheader?: string | boolean;
}

export const ButtonCloseModal = styled(Button)<ButtonCloseProps>`
  position: absolute;
  right: 1rem;
  top: ${props => (props.preheader ? '4.3rem' : '1rem')};
  z-index: 2;
`;

export const Centred = styled('div')`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;
